import React from 'react';
import { Loader } from 'lucide-react';
import type {
  LayoutStyle,
  PanfletoContentStyles,
  PanfletoFieldStyle,
  PanfletoProductNameAlign,
  PanfletoStyleFieldKey,
  Screen,
  TextStyle,
} from '../../../shared/types';
import {
  getPanfletoFieldBase,
  isPanfletoPriceSplitOn,
  mergePanfletoContent,
  mergePanfletoContentPreserving,
  normalizeHexColorString,
  normalizePanfletoFieldStyleRecord,
  normalizePanfletoProductNameAlign,
  preservePanfletoProductNameAlign,
  PANFLETO_STYLE_FIELD_LABELS,
} from '../../banners/utils/panfletoContentUtils';
import { PANFLETO_PRICE_FONT_OPTIONS, PANFLETO_TEXT_FONT_OPTIONS } from '../constants/panfletoFontOptions';
import { processImage, ImageProcessInfo } from '../utils/imageCompression';

function screenHasPanfletoProducts(s: Screen): boolean {
  return (s.linkedProducts?.length ?? 0) > 0 || (s.promotions?.length ?? 0) > 0;
}

interface PanfletoStyleEditorProps {
  screen: Screen;
  layoutStyle: LayoutStyle;
  onLayoutStyleChange: (next: LayoutStyle) => void;
  onGetCurrentStyle?: (getStyle: () => LayoutStyle) => void;
  forceSyncKey?: number;
  /** Chamado ao trocar/remover a imagem de fundo para o pai não sobrescrever com fetch remoto atrasado. */
  onPanfletoBackgroundUserChange?: () => void;
  /** Foto de fundo remota sendo buscada no endpoint dedicado da tela. */
  panfletoFotoLoading?: boolean;
}

const mergeText = (current: TextStyle | undefined, patch: Partial<TextStyle>): TextStyle => ({
  color: '#000000',
  fontSize: 16,
  fontFamily: 'Arial, sans-serif',
  ...current,
  ...patch,
});

function isIsoDateRangeInvalid(start?: string, end?: string): boolean {
  if (!start || !end) return false;
  return start > end;
}

/** Valor válido para `<input type="color">` (#rrggbb). Nunca usar cinza “aleatório” — mesmo padrão do fundo do card (colunas). */
function hexForColorInput(value: string | undefined, fallback: string): string {
  const n = normalizeHexColorString(value ?? '');
  if (n) return n;
  const f = normalizeHexColorString(fallback);
  return f || '#ffffff';
}

/** Mesma altura/largura: cor e tamanho (px) alinham no eixo, sem alterar o layout do restante da linha. */
const colorInputClass =
  'h-9 w-14 shrink-0 border border-gray-300 rounded-md p-0 cursor-pointer overflow-hidden [color-scheme:light] [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch-wrapper]:rounded-md [&::-webkit-color-swatch]:border-0 [&::-webkit-color-swatch]:rounded-md [&::-moz-color-swatch]:border-0 [&::-moz-color-swatch]:border-none [&::-moz-color-swatch]:border-radius:0.375rem';
const sizeInputClass = 'h-9 w-16 shrink-0 border border-gray-300 rounded-md px-2 text-sm text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none';
const fieldRowClass = 'flex flex-wrap items-center gap-3';
const textInputInRowClass =
  'flex-1 min-w-0 p-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent';
const fullWidthSelectClass =
  'w-full p-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent';

/** Mesma tipografia dos títulos de campo (Cabeçalho, Nome do produto, etc.). */
const corTamanhoSubLabelClass = 'text-sm text-gray-600 whitespace-nowrap shrink-0';

function tamanhoEfetivoPx(ov: PanfletoFieldStyle | undefined, base: { fontSize: number }): number {
  if (ov !== undefined && ov.fontSize !== undefined) {
    // fontSize=0 é usado para "ocultar"; no input mostrar 1 para evitar edição em zero manual.
    if (ov.fontSize === 0) return 1;
    if (ov.fontSize > 0) return ov.fontSize;
  }
  return base.fontSize;
}

const PanfletoStyleEditor: React.FC<PanfletoStyleEditorProps> = ({
  screen,
  layoutStyle,
  onLayoutStyleChange,
  onGetCurrentStyle,
  forceSyncKey,
  onPanfletoBackgroundUserChange,
  panfletoFotoLoading = false,
}) => {
  const content = mergePanfletoContent(layoutStyle.panfletoContent);
  const productContainer = layoutStyle.productContainer || {};
  const [thumbImageReady, setThumbImageReady] = React.useState(false);
  const validadeRangeInvalida = isIsoDateRangeInvalid(content.validadeDataInicial, content.validadeDataFinal);
  const [isCompressingImage, setIsCompressingImage] = React.useState(false);
  const [imageInfo, setImageInfo] = React.useState<ImageProcessInfo | null>(null);
  const layoutStyleRef = React.useRef(layoutStyle);
  layoutStyleRef.current = layoutStyle;
  /** Permite o campo vazio enquanto edita; vazio no blur vira 1. */
  const [tamanhoRascunho, setTamanhoRascunho] = React.useState<Partial<Record<PanfletoStyleFieldKey, string>>>({});
  React.useEffect(() => {
    setTamanhoRascunho({});
  }, [forceSyncKey]);
  React.useEffect(() => {
    setImageInfo(null);
  }, [screen.id, forceSyncKey]);

  const backgroundImageSrc = productContainer.backgroundImage;

  React.useEffect(() => {
    if (!backgroundImageSrc) {
      setThumbImageReady(false);
      return;
    }
    setThumbImageReady(false);
    let cancelled = false;
    const img = new Image();
    const markReady = () => {
      if (!cancelled) setThumbImageReady(true);
    };
    img.onload = markReady;
    img.onerror = markReady;
    img.src = backgroundImageSrc;
    // Blob/cache: onLoad pode não disparar de novo ao remontar a tela na mesma sessão.
    if (img.complete) {
      markReady();
    }
    return () => {
      cancelled = true;
      img.onload = null;
      img.onerror = null;
    };
  }, [screen.id, forceSyncKey, backgroundImageSrc]);

  React.useEffect(() => {
    if (onGetCurrentStyle) {
      onGetCurrentStyle(() => layoutStyleRef.current);
    }
  }, [onGetCurrentStyle]);

  const patchContent = (
    partial: Partial<typeof content> & {
      styles?: PanfletoContentStyles | undefined;
      backgroundColor?: string | undefined;
    }
  ) => {
    const baseLayout = layoutStyleRef.current;
    const baseContent = mergePanfletoContent(baseLayout.panfletoContent);
    let nextContent;
    if ('styles' in partial && partial.styles === undefined) {
      nextContent = { ...baseContent, ...partial };
      delete (nextContent as { styles?: PanfletoContentStyles }).styles;
    } else {
      nextContent = mergePanfletoContentPreserving(baseContent, partial);
    }
    if (
      'backgroundColor' in partial &&
      (partial.backgroundColor === undefined || String(partial.backgroundColor ?? '').trim() === '')
    ) {
      delete (nextContent as { backgroundColor?: string }).backgroundColor;
    }
    onLayoutStyleChange({
      ...baseLayout,
      panfletoContent: nextContent,
    });
  };

  const patchFieldStyle = (field: PanfletoStyleFieldKey, patch: Partial<PanfletoFieldStyle>) => {
    const baseContent = mergePanfletoContent(layoutStyleRef.current.panfletoContent);
    const prev = baseContent.styles?.[field] ?? {};
    const merged: PanfletoFieldStyle = { ...prev, ...patch };
    let cleaned = normalizePanfletoFieldStyleRecord(merged);
    if (field === 'linha1') {
      const kept = preservePanfletoProductNameAlign(prev, cleaned);
      if (kept) cleaned = kept;
    }
    const nextStyles: Record<string, PanfletoFieldStyle> = baseContent.styles ? { ...baseContent.styles } : {};
    if (Object.keys(cleaned).length === 0) {
      delete nextStyles[field];
    } else {
      nextStyles[field] = cleaned;
    }
    const keys = Object.keys(nextStyles);
    patchContent({ styles: keys.length ? (nextStyles as PanfletoContentStyles) : undefined });
  };

  const patchProductShowcase = (
    partial: NonNullable<NonNullable<typeof content.productShowcase>>
      | Partial<NonNullable<NonNullable<typeof content.productShowcase>>>
  ) => {
    const baseContent = mergePanfletoContent(layoutStyleRef.current.panfletoContent);
    const prev = baseContent.productShowcase || {};
    const nextShowcase: NonNullable<typeof content.productShowcase> = {
      sharedFrameScale: 1,
      panelGapPx: 0,
      sharedBorderWidth: 2,
      sharedBorderRadius: 26,
      sharedBorderColor: '#f1f5f9',
      textPanelBgColor: '#ef4444',
      productPanelBgColor: '#f8fafc',
      ...prev,
      ...partial,
    };
    delete nextShowcase.textPanelPaddingPx;
    delete nextShowcase.productPanelPaddingPx;
    patchContent({
      productShowcase: nextShowcase,
    });
  };

  const restaurarPadraoVisual = () => {
    setTamanhoRascunho({});
    patchContent({ styles: undefined, backgroundColor: undefined });
  };

  const patchProductContainer = (
    partial: Partial<LayoutStyle['productContainer']>
  ) => {
    const baseLayout = layoutStyleRef.current;
    const pc = baseLayout.productContainer || {};
    onLayoutStyleChange({
      ...baseLayout,
      productContainer: {
        ...pc,
        ...partial,
      },
    });
  };

  const priceFont =
    layoutStyle.price?.fontFamily ?? PANFLETO_PRICE_FONT_OPTIONS[5].value;
  const knownPriceFont = PANFLETO_PRICE_FONT_OPTIONS.some((o) => o.value === priceFont);

  const textFont =
    layoutStyle.productName?.fontFamily ?? PANFLETO_TEXT_FONT_OPTIONS[4].value;
  const knownTextFont = PANFLETO_TEXT_FONT_OPTIONS.some((o) => o.value === textFont);

  const patchPriceFont = (fontFamily: string) => {
    onLayoutStyleChange({
      ...layoutStyle,
      price: mergeText(layoutStyle.price, { fontFamily }),
    });
  };

  /** Uma fonte para todos os blocos de texto do cartaz (textos livres, linhas, unidade, validade). */
  const patchTextFont = (fontFamily: string) => {
    onLayoutStyleChange({
      ...layoutStyle,
      productName: mergeText(layoutStyle.productName, { fontFamily }),
      category: mergeText(layoutStyle.category, { fontFamily }),
      description: mergeText(layoutStyle.description, { fontFamily }),
      unit: mergeText(layoutStyle.unit, { fontFamily }),
      validUntil: mergeText(layoutStyle.validUntil, { fontFamily }),
    });
  };

  /** Preço De/Por com split: a preview lê priceValue*; o painel precisa gravar os dois. */
  const isPricePairField = (field: PanfletoStyleFieldKey): field is 'precoDe' | 'precoPor' =>
    field === 'precoDe' || field === 'precoPor';

  const patchPricePairValueStyle = (
    field: 'precoDe' | 'precoPor',
    patch: { color?: string; fontSize?: number }
  ) => {
    const next: Partial<PanfletoFieldStyle> = { ...patch };
    if (patch.color !== undefined) next.priceValueColor = patch.color;
    if (patch.fontSize !== undefined) next.priceValueFontSize = patch.fontSize;
    patchFieldStyle(field, next);
  };

  const commitTamanho = (field: PanfletoStyleFieldKey, raw: string, base: { fontSize: number }) => {
    const applySize = (n: number) => {
      if (isPricePairField(field)) patchPricePairValueStyle(field, { fontSize: n });
      else patchFieldStyle(field, { fontSize: n });
    };
    if (raw.trim() === '' || raw === undefined) {
      applySize(1);
    } else {
      const n = parseInt(raw, 10);
      if (Number.isNaN(n) || n < 1) {
        applySize(1);
      } else {
        applySize(n);
      }
    }
  };

  /** Define ou limpa data de validade (string vazia remove a chave do JSON). */
  const patchValidadeDate = (
    key: 'validadeDataInicial' | 'validadeDataFinal',
    value: string
  ) => {
    const next = String(value ?? '').trim();
    if (!next) {
      const baseLayout = layoutStyleRef.current;
      const baseContent = mergePanfletoContent(baseLayout.panfletoContent);
      const nextContent = { ...baseContent };
      delete nextContent[key];
      onLayoutStyleChange({
        ...baseLayout,
        panfletoContent: nextContent,
      });
      return;
    }
    patchContent({ [key]: next });
  };

  const renderColorSize = (
    field: PanfletoStyleFieldKey,
    letrasAntesDosInputs?: { cor: string; tamanho: string }
  ) => {
    const base = getPanfletoFieldBase(layoutStyle, field);
    const ov = content.styles?.[field];
    const pricePair = isPricePairField(field);
    const splitOn = pricePair && isPanfletoPriceSplitOn(ov);
    // Com split, a preview usa priceValue*; sem isso o painel mostrava o base do layout.price (ex.: 84 amarelo) e a edição não refletia.
    const colorSource = pricePair ? (ov?.priceValueColor ?? ov?.color) : ov?.color;
    const efetivo = (() => {
      if (ov?.fontSize === 0) return 1;
      if (pricePair && splitOn && ov?.priceValueFontSize !== undefined && ov.priceValueFontSize > 0) {
        return ov.priceValueFontSize;
      }
      return tamanhoEfetivoPx(ov, base);
    })();
    const tamanhoMostrado =
      field in tamanhoRascunho && tamanhoRascunho[field] !== undefined
        ? tamanhoRascunho[field]
        : String(efetivo);
    const colorInput = (
      <input
        key="c"
        type="color"
        className={colorInputClass}
        value={hexForColorInput(colorSource, base.color)}
        onChange={(e) => {
          const color = e.target.value;
          if (pricePair) patchPricePairValueStyle(field, { color });
          else patchFieldStyle(field, { color });
        }}
        title="Cor"
        aria-label={letrasAntesDosInputs ? letrasAntesDosInputs.cor : `Cor ${PANFLETO_STYLE_FIELD_LABELS[field]}`}
      />
    );
    const sizeInput = (
      <input
        key="s"
        type="text"
        inputMode="numeric"
        className={sizeInputClass}
        value={tamanhoMostrado}
        title="Tamanho (px)"
        aria-label={letrasAntesDosInputs ? letrasAntesDosInputs.tamanho : `Tamanho ${PANFLETO_STYLE_FIELD_LABELS[field]} (px)`}
        onChange={(e) => {
          const raw = e.target.value;
          if (!/^\d*$/.test(raw)) return;
          setTamanhoRascunho((d) => ({ ...d, [field]: raw }));
          if (raw === '') {
            return;
          }
          const n = parseInt(raw, 10);
          if (!Number.isNaN(n) && n > 0) {
            if (pricePair) patchPricePairValueStyle(field, { fontSize: n });
            else patchFieldStyle(field, { fontSize: n });
          }
        }}
        onBlur={(e) => {
          const raw = e.currentTarget.value;
          setTamanhoRascunho((d) => {
            const n = { ...d };
            delete n[field];
            return n;
          });
          commitTamanho(field, raw, base);
        }}
      />
    );
    if (letrasAntesDosInputs) {
      return (
        <>
          <div className="flex items-center gap-2">
            <span className={corTamanhoSubLabelClass}>{letrasAntesDosInputs.cor}</span>
            {colorInput}
          </div>
          <div className="flex items-center gap-2">
            <span className={corTamanhoSubLabelClass}>{letrasAntesDosInputs.tamanho}</span>
            {sizeInput}
          </div>
        </>
      );
    }
    return <>{colorInput}{sizeInput}</>;
  };

  const renderProdutoFixoStyle = (field: 'linha1' | 'linha2', titulo: string) => {
    const nameAlign: PanfletoProductNameAlign =
      field === 'linha1'
        ? (normalizePanfletoProductNameAlign(content.styles?.linha1?.textAlign) ?? 'left')
        : 'left';
    return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">{titulo}</label>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        {renderColorSize(field, { cor: 'Cor da fonte', tamanho: 'Tamanho da fonte' })}
        <label className="inline-flex items-center gap-2 text-sm text-gray-600 ml-1">
          Ocultar campo
          <input
            type="checkbox"
            className="w-5 h-5 accent-blue-600"
            checked={content.styles?.[field]?.fontSize === 0}
            onChange={(e) => patchFieldStyle(field, { fontSize: e.target.checked ? 0 : 1 })}
          />
        </label>
      </div>
      {field === 'linha1' ? (
        <div className="mt-2">
          <span className="block text-xs text-gray-600 mb-1">Alinhamento do nome</span>
          <div
            className="inline-flex overflow-hidden rounded-md border border-gray-300"
            role="group"
            aria-label="Alinhamento do nome do produto"
          >
            {(
              [
                { id: 'left', label: 'Esquerda' },
                { id: 'center', label: 'Centro' },
                { id: 'right', label: 'Direita' },
              ] as const
            ).map((opt) => (
              <button
                key={opt.id}
                type="button"
                className={`px-3 py-1.5 text-xs font-medium ${
                  nameAlign === opt.id ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
                onClick={() => patchFieldStyle('linha1', { textAlign: opt.id })}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
    );
  };

  const renderEstiloSóVisual = (
    field: PanfletoStyleFieldKey,
    titulo: string,
    opts?: { desc?: React.ReactNode; showHideSwitch?: boolean }
  ) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-0.5">{titulo}</label>
      {opts?.desc ? <p className="text-xs text-gray-500 mb-1.5">{opts.desc}</p> : null}
      <div className={`flex flex-wrap items-center gap-x-3 gap-y-2 ${opts?.desc ? '' : 'mt-1'}`}>
        {renderColorSize(field, { cor: 'Cor da fonte', tamanho: 'Tamanho da fonte' })}
        {opts?.showHideSwitch ? (
          <label className="inline-flex items-center gap-2 text-sm text-gray-600 ml-1">
            Ocultar campo
            <input
              type="checkbox"
              className="w-5 h-5 accent-blue-600"
              checked={content.styles?.[field]?.fontSize === 0}
              onChange={(e) => patchFieldStyle(field, { fontSize: e.target.checked ? 0 : 1 })}
            />
          </label>
        ) : null}
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h3 className="text-lg font-semibold mb-4">Estilo do Layout</h3>
      <div className="border-b pb-4 mb-4" key={`bg-${screen.id}-${forceSyncKey ?? 0}`}>
        <div className="flex justify-start mb-1.5">
          <button
            type="button"
            onClick={restaurarPadraoVisual}
            className="text-sm font-medium text-gray-700 py-1.5 px-3 rounded-md border border-gray-300 bg-gray-50 hover:bg-gray-100"
            title="Restaura a cor de fundo e as cores/tamanhos dos campos; voltam as definições do layout."
          >
            Restaurar padrão
          </button>
        </div>
        <span className="block text-sm text-gray-600 mb-1">Cor de fundo (opcional)</span>
        <div className={fieldRowClass}>
          <input
            type="color"
            className={colorInputClass}
            value={hexForColorInput(content.backgroundColor, '#ffffff')}
            onChange={(e) => {
              const v = e.target.value;
              patchContent({ backgroundColor: normalizeHexColorString(v) || v });
            }}
            title="Cor de fundo do cartaz (fica sob a imagem quando há opacidade)"
            aria-label="Cor de fundo"
          />
        </div>
        <div className="mt-4 pt-3 border-t border-gray-100">
          <h5 className="font-medium text-sm mb-3">Imagem de Fundo</h5>
          <p className="text-xs text-gray-500 mb-2">
            Panfleto usa endpoint dedicado da tela (fora de <code>ds_configuracoes</code>).
          </p>

          <div className="mb-4">
            <label className="block text-sm text-gray-600 mb-2">Imagem de Fundo</label>
            <input
              type="file"
              accept="image/*"
              disabled={isCompressingImage}
              onChange={async (e) => {
                const inputEl = e.currentTarget;
                const file = e.target.files?.[0];
                if (!file) return;
                setIsCompressingImage(true);
                try {
                  const imageProcessInfo = await processImage(file);
                  setImageInfo(imageProcessInfo);
                  onPanfletoBackgroundUserChange?.();
                  const pcNow = layoutStyleRef.current.productContainer || {};
                  patchProductContainer({
                    backgroundImage: imageProcessInfo.base64,
                    backgroundImageVisible: true,
                    backgroundImageOpacity: pcNow.backgroundImageOpacity ?? 100,
                    backgroundImageBlur: pcNow.backgroundImageBlur ?? 0,
                  });
                } catch (error) {
                  console.error('Erro ao processar imagem do panfleto:', error);
                  alert('Erro ao processar a imagem. Por favor, tente novamente.');
                } finally {
                  setIsCompressingImage(false);
                  if (inputEl) inputEl.value = '';
                }
              }}
              className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            {isCompressingImage && (
              <p className="mt-2 text-xs text-blue-600 flex items-center gap-2">
                <Loader className="w-3.5 h-3.5 animate-spin shrink-0" />
                Carregando imagem... Por favor, aguarde.
              </p>
            )}
            {panfletoFotoLoading && !productContainer.backgroundImage && !isCompressingImage && (
              <p className="mt-2 text-xs text-blue-600 flex items-center gap-2">
                <Loader className="w-3.5 h-3.5 animate-spin shrink-0" />
                Carregando foto de fundo...
              </p>
            )}
            {imageInfo ? (
              <p className="mt-2 text-xs text-gray-500">
                {imageInfo.wasCompressed
                  ? `Imagem comprimida: ${imageInfo.originalSizeMB.toFixed(2)}MB -> ${imageInfo.compressedSizeMB.toFixed(2)}MB`
                  : `Imagem carregada: ${imageInfo.originalSizeMB.toFixed(2)}MB`}
              </p>
            ) : null}
            {productContainer.backgroundImage && (
              <div className="mt-2 relative">
                {!thumbImageReady ? (
                  <div className="w-full h-32 flex items-center justify-center gap-2 rounded border border-gray-300 bg-gray-50 text-xs text-gray-500">
                    <Loader className="w-3.5 h-3.5 animate-spin shrink-0 text-blue-500" />
                    Carregando foto...
                  </div>
                ) : (
                  <img
                    src={productContainer.backgroundImage}
                    alt="Preview"
                    className="w-full h-32 object-cover rounded border border-gray-300"
                  />
                )}
                <button
                  type="button"
                  onClick={() => {
                    onPanfletoBackgroundUserChange?.();
                    patchProductContainer({
                      backgroundImage: undefined,
                      backgroundImageVisible: true,
                      backgroundImageOpacity: 100,
                      backgroundImageBlur: 0,
                      backgroundVignetteEnabled: false,
                      backgroundVignetteIntensity: 35,
                    });
                  }}
                  className="absolute top-2 right-2 bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600"
                >
                  Remover
                </button>
              </div>
            )}
          </div>

          <div className="mb-4">
            <div className="flex items-center gap-2">
              <input
                id={`panfleto-bg-visible-${screen.id}`}
                type="checkbox"
                checked={productContainer.backgroundImageVisible !== false}
                onChange={(e) => patchProductContainer({ backgroundImageVisible: e.target.checked })}
                className="w-4 h-4 accent-blue-600"
                disabled={!productContainer.backgroundImage}
              />
              <label htmlFor={`panfleto-bg-visible-${screen.id}`} className="text-sm text-gray-600">
                Exibir imagem de fundo
              </label>
            </div>
          </div>

          {productContainer.backgroundImage && (
            <div className="mb-4">
              <label className="block text-sm text-gray-600 mb-1">Opacidade do fundo do produto</label>
              <input
                type="range"
                min="0"
                max="100"
                value={productContainer.backgroundImageOpacity ?? 100}
                onChange={(e) => patchProductContainer({ backgroundImageOpacity: Number(e.target.value) })}
                className="w-full"
                disabled={productContainer.backgroundImageVisible === false}
              />
              <span className="text-xs text-gray-500 block mt-1">{productContainer.backgroundImageOpacity ?? 100}%</span>
            </div>
          )}

          {productContainer.backgroundImage && (
            <div className="mb-4">
              <label className="block text-sm text-gray-600 mb-1">Desfoque (Blur)</label>
              <input
                type="range"
                min="0"
                max="20"
                value={productContainer.backgroundImageBlur ?? 0}
                onChange={(e) => patchProductContainer({ backgroundImageBlur: Number(e.target.value) })}
                className="w-full"
                disabled={productContainer.backgroundImageVisible === false}
              />
              <span className="text-xs text-gray-500 block mt-1">{productContainer.backgroundImageBlur ?? 0}px</span>
            </div>
          )}

          {productContainer.backgroundImage && (
            <div className="mb-2 border-t pt-4">
              <div className="flex items-center gap-2 mb-2">
                <input
                  id={`panfleto-vignette-enabled-${screen.id}`}
                  type="checkbox"
                  checked={productContainer.backgroundVignetteEnabled === true}
                  onChange={(e) => patchProductContainer({ backgroundVignetteEnabled: e.target.checked })}
                  className="w-4 h-4 accent-blue-600"
                  disabled={productContainer.backgroundImageVisible === false}
                />
                <label htmlFor={`panfleto-vignette-enabled-${screen.id}`} className="text-sm text-gray-600">
                  Efeito vignette (bordas escuras)
                </label>
              </div>
              {productContainer.backgroundVignetteEnabled ? (
                <>
                  <label className="block text-sm text-gray-600 mb-1">Intensidade da vinheta</label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={productContainer.backgroundVignetteIntensity ?? 35}
                    onChange={(e) =>
                      patchProductContainer({ backgroundVignetteIntensity: Number(e.target.value) })
                    }
                    className="w-full"
                    disabled={productContainer.backgroundImageVisible === false}
                  />
                  <span className="text-xs text-gray-500 block mt-1">
                    {productContainer.backgroundVignetteIntensity ?? 35}%
                  </span>
                </>
              ) : null}
            </div>
          )}
        </div>
      </div>

      {screenHasPanfletoProducts(screen) ? (
        <div className="space-y-3 border-b border-gray-200 pb-4 mb-4">
          <h4 className="font-medium mb-3">Borda compartilhada</h4>
          <div className="mb-3">
            <label className="block text-xs text-gray-600 mb-1">
              Escala da moldura compartilhada ({Math.round((content.productShowcase?.sharedFrameScale ?? 1) * 100)}%)
            </label>
            <input
              type="range"
              min={0.35}
              max={1}
              step={0.05}
              className="w-full accent-blue-600"
              value={content.productShowcase?.sharedFrameScale ?? 1}
              onChange={(e) =>
                patchProductShowcase({
                  sharedFrameScale: Math.min(1, Math.max(0.35, Number(e.target.value) || 1)),
                })
              }
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-3 gap-y-4 mb-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Espessura da borda compartilhada</label>
              <input
                type="number"
                min={0}
                max={20}
                className={textInputInRowClass}
                value={content.productShowcase?.sharedBorderWidth ?? 2}
                onChange={(e) => patchProductShowcase({ sharedBorderWidth: Math.max(0, Number(e.target.value) || 0) })}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Arredondamento da borda compartilhada</label>
              <input
                type="number"
                min={0}
                max={100}
                className={textInputInRowClass}
                value={content.productShowcase?.sharedBorderRadius ?? 26}
                onChange={(e) => patchProductShowcase({ sharedBorderRadius: Math.max(0, Number(e.target.value) || 0) })}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-600 mb-1">Cor da borda compartilhada</label>
              <input
                type="color"
                className={colorInputClass}
                value={hexForColorInput(content.productShowcase?.sharedBorderColor, '#f1f5f9')}
                onChange={(e) => patchProductShowcase({ sharedBorderColor: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Fundo painel de texto</label>
              <input
                type="color"
                className={colorInputClass}
                value={hexForColorInput(content.productShowcase?.textPanelBgColor, '#ef4444')}
                onChange={(e) => patchProductShowcase({ textPanelBgColor: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Fundo painel de produto</label>
              <input
                type="color"
                className={colorInputClass}
                value={hexForColorInput(content.productShowcase?.productPanelBgColor, '#f8fafc')}
                onChange={(e) => patchProductShowcase({ productPanelBgColor: e.target.value })}
              />
            </div>
          </div>
        </div>
      ) : null}

      <div className="space-y-3 border-b border-gray-200 pb-4 mb-4">
        <h4 className="font-medium mb-3">Tipografia</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {screenHasPanfletoProducts(screen) ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fonte dos preços
              </label>
              <select
                className={fullWidthSelectClass}
                value={priceFont}
                onChange={(e) => patchPriceFont(e.target.value)}
              >
                {!knownPriceFont ? (
                  <option value={priceFont}>Atual (personalizada)</option>
                ) : null}
                {PANFLETO_PRICE_FONT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          <div className={screenHasPanfletoProducts(screen) ? undefined : 'md:col-span-2'}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fonte dos textos
            </label>
            <select
              className={fullWidthSelectClass}
              value={textFont}
              onChange={(e) => patchTextFont(e.target.value)}
            >
              {!knownTextFont ? (
                <option value={textFont}>Atual (personalizada)</option>
              ) : null}
              {PANFLETO_TEXT_FONT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="space-y-3" key={`${screen.id}-${forceSyncKey ?? 0}`}>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Texto 1 (opcional)
          </label>
          <div className={fieldRowClass}>
            <input
              type="text"
              className={textInputInRowClass}
              value={content.texto1 ?? ''}
              onChange={(e) => patchContent({ texto1: e.target.value })}
              placeholder="Texto 1"
            />
            {renderColorSize('texto1')}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Texto 2 (opcional)</label>
          <div className={fieldRowClass}>
            <input
              type="text"
              className={textInputInRowClass}
              value={content.texto2 ?? ''}
              onChange={(e) => patchContent({ texto2: e.target.value })}
              placeholder="Texto 2"
            />
            {renderColorSize('texto2')}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Texto 3 (opcional)</label>
          <div className={fieldRowClass}>
            <input
              type="text"
              className={textInputInRowClass}
              value={content.texto3 ?? ''}
              onChange={(e) => patchContent({ texto3: e.target.value })}
              placeholder="Texto 3"
            />
            {renderColorSize('texto3')}
          </div>
        </div>

        {screenHasPanfletoProducts(screen) ? (
          <>
            {renderProdutoFixoStyle('linha1', 'Nome do produto')}
            {renderProdutoFixoStyle('linha2', 'Categoria do produto')}
            {renderEstiloSóVisual('precoDe', 'Preço "De" (riscado)', {
              desc: 'Aparece apenas quando o produto tem promoção.',
              showHideSwitch: true,
            })}
            {renderEstiloSóVisual('precoPor', 'Preço "Por"', { showHideSwitch: true })}
          </>
        ) : null}

        <div className="space-y-2 mb-3">
          <label className="block text-sm font-medium text-gray-700 mb-1">Validade</label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-gray-600 mb-0.5">Data inicial</label>
              <div className="flex items-center gap-3">
                <input
                  type="date"
                  className="flex-1 min-w-0 p-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={content.validadeDataInicial ?? ''}
                  onChange={(e) => patchValidadeDate('validadeDataInicial', e.target.value)}
                />
                <button
                  type="button"
                  className="shrink-0 text-xs font-medium text-gray-600 px-2.5 py-2 rounded-md border border-gray-300 bg-gray-50 hover:bg-gray-100"
                  onClick={() => patchValidadeDate('validadeDataInicial', '')}
                  title="Limpar data inicial"
                >
                  Limpar
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-0.5">Data final</label>
              <div className="flex items-center gap-3">
                <input
                  type="date"
                  className="flex-1 min-w-0 p-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={content.validadeDataFinal ?? ''}
                  onChange={(e) => patchValidadeDate('validadeDataFinal', e.target.value)}
                />
                <button
                  type="button"
                  className="shrink-0 text-xs font-medium text-gray-600 px-2.5 py-2 rounded-md border border-gray-300 bg-gray-50 hover:bg-gray-100"
                  onClick={() => patchValidadeDate('validadeDataFinal', '')}
                  title="Limpar data final"
                >
                  Limpar
                </button>
              </div>
            </div>
          </div>
          {validadeRangeInvalida ? (
            <p className="text-xs text-red-600">
              A data inicial nao pode ser maior que a data final.
            </p>
          ) : null}
        </div>

        <div className="mb-3">
          {renderEstiloSóVisual('validade', 'Aparência da validade', { showHideSwitch: true })}
        </div>

        {screenHasPanfletoProducts(screen) ? (
          <div className="mb-3">
            {renderEstiloSóVisual('quantidade', 'Quantidade e unidade (ex.: 1 UN no cartaz)', {
              showHideSwitch: true,
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default PanfletoStyleEditor;
